// Interface specs: interactive POS views built from the document vocabulary plus declared
// state, form controls, event handlers with a fixed action vocabulary, and named operations
// from the catalog in operations.ts. Everything is JSON; expressions (expr.ts) run only in the
// browser. The backend broker enforces capabilities, levels, approval and rate limits on
// every operation call, so the schema and lint here are for authors and approvers.
import * as s from './schema-dsl.ts'
import { buildNodeVariants, nodeSchema, COLUMN_KEY_RE, LOCALE_RE, MAX_DEPTH, CONTAINER_TYPES } from './document.ts'
import { INTENTS, PRESET_DESCRIPTION, presetIds } from './tokens.ts'
import { VALUE_FORMATS } from './format.ts'
import {
  CAPABILITIES, CAPABILITY_LEVELS, CAPABILITY_RISK, MAX_SPEC_BYTES, MAX_SPEC_NODES, SOURCE_ID_RE, SPEC_VERSION,
  registerSpecKind, type Capability, type Risk, type SpecAnalysis, type SpecDescription, type SpecLintResult,
  type SpecOperationFact, type SpecOutlineItem
} from './spec.ts'
import { OPERATIONS, checkOperationInput, exportCapability, exportPersonal, operationCapabilities } from './operations.ts'
import { exprSchema, exprVars, EXPR_SCOPES, FMT_FORMATS, MAX_DERIVE_STEPS, MAX_EXPR_STEPS } from './expr.ts'
import {
  ALIGNS, ASIDE_WIDTHS, BREAKPOINTS, GAPS, ITEM_WIDTHS, JUSTIFIES, MAX_GRID_COLUMNS, MIN_WIDTHS, SPLIT_RATIOS
} from './layout.ts'
import { lintDesign } from './design-lint.ts'
import { BACKGROUND_WINDOW_SECONDS, REFRESH_MAX_SECONDS, REFRESH_MIN_SECONDS, interfaceReadTypes } from './refresh.ts'

export const INTERFACE_KIND = 'interface'
export const MAX_STATE = 32
export const MAX_OPERATIONS = 32
export const MAX_HANDLER_ACTIONS = 12
/** then/catch/confirm nesting below a handler */
export const MAX_ACTION_NESTING = 4
/** Refs count expressions and actions too; node depth is checked separately against MAX_DEPTH */
const INTERFACE_SCHEMA_DEPTH = 48
/** Caps lists filled in one shot (on.load, tables). Catalog pickers page instead and skip this. */
export const MAX_LIST_STATE = 1000

export const STATE_TYPES = ['string', 'number', 'boolean', 'list', 'map', 'record'] as const
export type StateType = typeof STATE_TYPES[number]
export const FIELD_INPUTS = [
  'text', 'number', 'search', 'select', 'toggle', 'textarea', 'date', 'datetime', 'time', 'multiselect',
  'chips', 'segmented', 'radio', 'checkbox', 'checkboxes', 'stepper', 'slider'
] as const
/** Inputs that pick from options */
export const OPTION_INPUTS = ['select', 'multiselect', 'chips', 'segmented', 'radio', 'checkboxes'] as const
export const FILTER_INPUTS = ['search', 'select', 'daterange', 'date', 'datetime', 'time', 'multiselect'] as const
export const MAX_FILTERS = 6
export const EDIT_INPUTS = ['number', 'text', 'toggle', 'date', 'time', 'select'] as const
export const NAVIGATE_TARGETS = ['product', 'order', 'customer', 'resource', 'reservation', 'bill', 'category', 'issue', 'discount', 'content', 'show', 'message', 'billingcustomer', 'collection', 'group'] as const
export const ACTION_TYPES = ['setState', 'setIn', 'callOperation', 'openConfirm', 'navigate', 'toast', 'refresh', 'print', 'copy', 'openPanel', 'closePanel'] as const
/** Interactive nodes a host may draw with its own markup (MAX_CONTROLS_KEY) */
export const CONTROL_TYPES = ['field', 'button', 'datatable', 'filterbar', 'calendar', 'board', 'detail', 'repeat', 'datenav', 'menu'] as const
export const PRINT_KINDS = ['receipt', 'invoice', 'transfer', 'delivery', 'refund'] as const
export const CALENDAR_KINDS = ['showtimes', 'product_schedule', 'resource_schedule', 'agenda'] as const
export const BOARD_STATUSES = ['paid', 'notpaid', 'processed', 'notprocessed', 'open', 'confirmed'] as const
/** Closed icon set; POS draws them with its icon font, other hosts with the built-in SVGs */
export const ICONS = [
  'add', 'alert', 'archive', 'arrow-left', 'arrow-right', 'bell', 'bus', 'calendar', 'check', 'chevron-down',
  'chevron-left', 'chevron-right', 'chevron-up', 'clock', 'close', 'copy', 'delete', 'download', 'edit', 'euro',
  'filter', 'hide', 'home', 'info', 'key', 'list', 'location', 'lock', 'mail', 'minus', 'phone', 'print',
  'refresh', 'save', 'search', 'settings', 'show', 'star', 'tag', 'undo', 'user', 'users'
] as const
export type IconName = typeof ICONS[number]
/** Kept for older specs; button icons are now the whole icon set */
export const BUTTON_ICONS = ICONS
/** Containers an interface adds to the document layout ones; each has children */
export const INTERFACE_CONTAINERS = ['row', 'sidebar', 'card', 'scroll', 'sticky', 'disclosure', 'empty'] as const
/** Nodes whose children are a template rendered once per item */
export const TEMPLATE_TYPES = ['repeat', 'list', 'timeline'] as const
export const BUTTON_INTENTS = ['neutral', 'accent', 'positive', 'warning', 'negative'] as const
export const CARD_TONES = ['surface', 'sunken', 'outline', 'accent', 'neutral', 'positive', 'warning', 'negative', 'info'] as const
/** Props that may be an { expr } giving one of these values; others resolve to nothing */
export const DYNAMIC_ENUMS: Record<string, readonly string[]> = { intent: INTENTS, icon: ICONS, tone: CARD_TONES }
export const PADS = ['none', 'sm', 'md', 'lg'] as const
export const TEXT_SIZES = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'] as const
export const WEIGHTS = ['regular', 'medium', 'bold'] as const
export const BUTTON_VARIANTS = ['solid', 'soft', 'outline', 'ghost', 'link'] as const
export const SIZES = ['sm', 'md', 'lg'] as const
export const SCROLL_HEIGHTS = ['sm', 'md', 'lg', 'xl'] as const
/** Their steps share MAX_DERIVE_STEPS per render (expr.ts) */
export const MAX_DERIVE = 40
export const MAX_COMPONENTS = 16
export const MAX_COMPONENT_PARAMS = 12
export const MAX_PANELS = 8
/** Components may use other components, this many levels deep */
export const MAX_COMPONENT_NESTING = 4
export const PARAM_RE = /^[a-z][A-Za-z0-9_]{0,31}$/
/** Custom icon names; a tool declares each in spec.icons */
export const CUSTOM_ICON_RE = /^[a-z][a-z0-9-]{0,31}$/
export const MAX_CUSTOM_ICONS = 24
/** SVG path data only: drawing commands, numbers and separators. Nothing else of SVG can be written */
export const ICON_PATH_RE = /^[Mm][MmLlHhVvCcSsQqTtAaZz0-9eE.,+\s-]*$/
export const MAX_ICON_PATH = 4000
/** min-x min-y width height, e.g. 0 0 24 24 or 0 0 512 512 */
export const ICON_VIEWBOX_RE = /^-?\d{1,4}(\.\d{1,3})?([ ,]+-?\d{1,4}(\.\d{1,3})?){3}$/
export const ICON_SIZES = ['sm', 'md', 'lg', 'xl'] as const
export const MAP_KEY_RE = /^[A-Za-z0-9_-]{1,64}$/
/** What a time field stores: 24-hour HH:MM */
export const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/

/** A time input's value as HH:MM (seconds dropped), or '' when it is empty or not a time */
export function normalizeTime (value: unknown): string {
  const m = typeof value === 'string' ? /^(\d{2}:\d{2})(:\d{2}(\.\d+)?)?$/.exec(value.trim()) : null
  return m && TIME_RE.test(m[1]!) ? m[1]! : ''
}
export const METADATA_NS_RE = /^[a-z][a-z0-9_]{0,31}$/
export const MAX_METADATA_NAMESPACES = 8

export interface StateDef { type: StateType, initial?: unknown }
export interface OperationRef { operation: string, label?: string, confirmAbove?: number }
export type Action = { do: typeof ACTION_TYPES[number] } & Record<string, unknown>

export interface ComponentDef { params?: string[], body: Record<string, unknown>[] }
export interface CustomIcon { path: string, viewBox?: string, style?: 'stroke' | 'fill' }
export interface PanelDef { title: unknown, size?: 'sm' | 'md' | 'lg', body: Record<string, unknown>[] }

export interface MaxInterfaceSpec {
  version: 1
  kind: 'interface'
  title: string
  description?: string
  locale: string
  preset?: string
  capabilities: Capability[]
  state?: Record<string, StateDef>
  operations?: Record<string, OperationRef>
  metadataNamespaces?: string[]
  /** Named values computed from state in order; read as derived.<name> */
  derive?: Record<string, unknown>
  /** Reusable node templates: { type: 'use', component, with } renders one with props.<param> */
  components?: Record<string, ComponentDef>
  /** Drawers opened with openPanel */
  panels?: Record<string, PanelDef>
  /** declared (default): metadata of metadataNamespaces only; all: read every namespace, write still only the declared ones */
  metadataRead?: 'declared' | 'all'
  /** The tool's own icons, used by name like the built-in ones */
  icons?: Record<string, CustomIcon>
  on?: { load?: Action[] }
  /** Seconds between background reruns of on.load while the tool is in view and idle; at least 60 */
  refresh?: number
  body: Record<string, unknown>[]
}

// ---------------------------------------------------------------------------
// Schema

const scalar = s.anyOf([s.string({ max: 500 }), s.number(), s.boolean(), s.nullValue()])
const intent = s.enumOf(INTENTS)
const stateName = s.string({ max: 32, pattern: SOURCE_ID_RE, description: 'Key in spec.state' })
const operationName = s.string({ max: 32, pattern: SOURCE_ID_RE, description: 'Key in spec.operations' })
const expr = () => s.ref('Expr')
const computed = () => s.object({ expr: expr() }, { required: ['expr'], description: 'Value computed from state when the interface renders' })
const dynamicText = (max: number) => s.anyOf([s.string({ max }), computed()])
const actions = () => s.array(s.ref('Action'), { max: MAX_HANDLER_ACTIONS })

function actionVariants (): Record<string, s.Schema> {
  const action = (name: string, props: Record<string, s.Schema>, required: string[] = []) =>
    s.object({ do: s.literal(name), when: expr(), ...props }, { required: ['do', ...required] })
  return {
    setState: action('setState', { state: stateName, value: expr() }, ['state', 'value']),
    setIn: action('setIn', { state: stateName, key: expr(), value: expr() }, ['state', 'key', 'value']),
    callOperation: action('callOperation', {
      operation: operationName,
      input: s.record(expr(), { maxKeys: 16, keyPattern: /^[a-z][a-z_]{0,31}$/, description: 'Operation input; each value is an expression' }),
      into: s.string({ max: 32, pattern: SOURCE_ID_RE, description: 'State that receives the result: list stores rows, record stores rows[0], other types store the whole result; writes store the change summary' }),
      then: actions(),
      catch: actions()
    }, ['operation']),
    openConfirm: action('openConfirm', {
      title: s.string({ max: 120 }),
      message: dynamicText(600),
      confirmLabel: s.string({ max: 40 }),
      intent: s.enumOf(['accent', 'warning', 'negative']),
      then: actions()
    }, ['title', 'then']),
    navigate: action('navigate', { to: s.enumOf(NAVIGATE_TARGETS), id: expr() }, ['to', 'id']),
    toast: action('toast', { message: dynamicText(300), intent }, ['message']),
    refresh: action('refresh', {}),
    print: action('print', {
      kind: s.enumOf(PRINT_KINDS, 'POS PDF: receipt, invoice, transfer, delivery or refund'),
      id: expr()
    }, ['kind', 'id']),
    copy: action('copy', {
      value: expr(),
      toast: s.string({ max: 120, description: 'Shown once copied, e.g. "Kopioitu"' })
    }, ['value']),
    openPanel: action('openPanel', { panel: s.string({ max: 32, pattern: SOURCE_ID_RE, description: 'Key in spec.panels' }) }, ['panel']),
    closePanel: action('closePanel', {})
  }
}

function interfaceNodeVariants (): Record<string, s.Schema> {
  const extra = {
    visible: expr(),
    width: s.enumOf(ITEM_WIDTHS, 'Width inside a row: fill takes the rest, xs–lg are fixed, auto fits the content'),
    hideBelow: s.enumOf(BREAKPOINTS, 'Hidden while the tool is narrower than this breakpoint (sm 480, md 768, lg 1100, xl 1440 px)'),
    hideFrom: s.enumOf(BREAKPOINTS, 'Hidden from this breakpoint up')
  }
  const node = (type: string, props: Record<string, s.Schema>, required: string[] = [], refine?: s.ObjectOpts['refine']) =>
    nodeSchema(type, props, required, refine, extra)
  const children = (max = 200) => s.array(s.ref('Node'), { max })
  const handlers = (events: Record<string, string>) => s.object(
    Object.fromEntries(Object.keys(events).map(k => [k, actions()])),
    { description: Object.entries(events).map(([k, v]) => `${k}: ${v}`).join('. ') }
  )
  const open = () => s.object({ to: s.enumOf(NAVIGATE_TARGETS), id: expr() }, { required: ['to'], description: 'Clicking opens the record in a POS details column' })
  const responsive = (schema: s.Schema, description: string) => s.anyOf([
    schema,
    s.object({ base: schema, sm: schema, md: schema, lg: schema, xl: schema }, { description: 'Mobile first: base everywhere, then from each breakpoint up' })
  ], description)
  // Colours and icons often follow the data (paid or not, ready or not), so these take an
  // expression too; the runtime keeps only values from the list.
  const dynEnum = (values: readonly string[], description?: string) => s.anyOf([s.enumOf(values), computed()], description)
  const intent = dynEnum(INTENTS)
  const iconName = s.anyOf([
    s.enumOf(ICONS),
    s.string({ max: 32, pattern: CUSTOM_ICON_RE, description: 'A custom icon declared in spec.icons' })
  ])
  const icon = s.anyOf([iconName, computed()], 'A built-in icon, a custom icon from spec.icons, or {expr} giving either')
  const buttonIntent = dynEnum(BUTTON_INTENTS)
  const optionItem = () => s.object({ label: s.string({ max: 80 }), value: scalar }, { required: ['label', 'value'] })
  const options = () => s.anyOf([
    s.array(s.anyOf([
      optionItem(),
      s.object({
        label: s.string({ max: 80 }),
        options: s.array(optionItem(), { min: 1, max: 100 })
      }, { required: ['label', 'options'] })
    ]), { max: 100 }),
    computed()
  ], 'select only: {label, value} or optgroups {label, options: [{label, value}]}, or {expr} giving the same')
  const selectOptions: s.ObjectOpts['refine'] = (v, _p, report) => {
    const picks = (OPTION_INPUTS as readonly string[]).includes(String(v.input))
    if (picks && v.options === undefined) report('options', `is required for ${String(v.input)}`)
    if (!picks && v.options !== undefined) report('options', 'is only used by inputs that pick from options')
    if (v.input === 'daterange' && v.period === undefined) report('period', 'is required for daterange')
    if (v.input !== 'daterange' && (v.period !== undefined || v.from !== undefined || v.to !== undefined)) {
      report('period', 'is only used by daterange')
    }
  }
  /** time fields, filters and cells: step in whole minutes 1–720, no min/max */
  const timeRules: s.ObjectOpts['refine'] = (v, _p, report) => {
    if (v.input !== 'time') return
    if (v.step !== undefined && (!Number.isInteger(v.step) || (v.step as number) < 1 || (v.step as number) > 720)) report('step', 'time step is whole minutes from 1 to 720')
    if (v.min !== undefined || v.max !== undefined) report('min', 'min and max are not used by time')
  }
  const timeStep = s.number({ min: 0, description: 'time only: minutes between the times offered, a whole number from 1 to 720 (default 1)' })
  return {
    ...buildNodeVariants(extra),
    heading: node('heading', { text: dynamicText(200), level: s.enumOf([1, 2, 3]), eyebrow: dynamicText(60) }, ['text']),
    text: node('text', {
      text: dynamicText(6000),
      spans: s.array(s.object({
        text: dynamicText(1000),
        strong: s.boolean(),
        mono: s.boolean(),
        tone: s.enumOf(['default', 'muted']),
        intent
      }, { required: ['text'] }), { min: 1, max: 24, description: 'Inline runs in one line of text, e.g. a bold count before a muted label. Use instead of text' }),
      tone: s.enumOf(['default', 'lead', 'muted', 'small']),
      size: s.enumOf(TEXT_SIZES),
      weight: s.enumOf(WEIGHTS),
      mono: s.boolean('Tabular monospace digits, for times, codes and amounts that line up'),
      intent,
      align: s.enumOf(['start', 'center', 'end']),
      truncate: s.boolean('One line with an ellipsis')
    }, [], (v, _p, report) => {
      if ((v.text === undefined) === (v.spans === undefined)) report('text', 'set text or spans')
    }),
    callout: node('callout', { intent, title: s.string({ max: 120 }), text: dynamicText(1500) }, ['text']),
    metric: node('metric', {
      label: s.string({ max: 80 }),
      value: s.anyOf([s.number(), s.string({ max: 40 }), s.nullValue(), computed()]),
      format: s.enumOf(VALUE_FORMATS),
      currency: s.string({ min: 3, max: 3, pattern: /^[A-Z]{3}$/ }),
      intent,
      caption: dynamicText(160)
    }, ['label', 'value']),
    field: node('field', {
      input: s.enumOf(FIELD_INPUTS),
      label: s.string({ max: 80 }),
      state: stateName,
      placeholder: s.string({ max: 80 }),
      help: s.string({ max: 200 }),
      options: options(),
      min: s.number(),
      max: s.number(),
      step: s.number({ min: 0, description: 'number, stepper and slider: the increment. time: minutes between the times offered, a whole number from 1 to 720 (default 1)' }),
      hideLabel: s.boolean('Label for screen readers only, e.g. chips that pick a person'),
      key: expr(),
      disabled: expr(),
      on: s.object({ change: actions() }, { description: 'change runs when the user commits a value; event.value holds it' })
    }, ['input', 'label', 'state'], (v, p, report) => {
      selectOptions(v, p, report)
      timeRules(v, p, report)
    }),
    filterbar: node('filterbar', {
      filters: s.array(s.object({
        input: s.enumOf(FILTER_INPUTS),
        label: s.string({ max: 80, description: 'search: read by screen readers, the placeholder is what shows. select: shown before the options. daterange: shown before the period' }),
        state: stateName,
        period: stateName,
        from: stateName,
        to: stateName,
        placeholder: s.string({ max: 80 }),
        step: timeStep,
        options: options(),
        on: s.object({ change: actions() }, { description: 'search: runs as the user types, after a short pause, and on Enter. select and multiselect: POS draws a typeahead; when options come from a catalog list it searches that list as the user types and pages the matches. on.change runs when an option is chosen. daterange: runs when the period or dates change. time: runs when a whole time is committed or the time is cleared. event.value holds the committed value' })
      }, { required: ['input', 'label'], refine: (v, p, report) => {
        selectOptions(v, p, report)
        timeRules(v, p, report)
        if (v.input !== 'time' && v.step !== undefined) report('step', 'is only used by time')
        if (v.input !== 'daterange' && v.state === undefined) report('state', 'is required')
      } }), { min: 1, max: MAX_FILTERS })
    }, ['filters']),
    button: node('button', {
      label: dynamicText(60),
      intent: buttonIntent,
      icon: s.anyOf([iconName, computed()], 'Icon before the label, as on POS buttons: save (with intent positive) for saving edits, show and hide for visibility, archive, delete, add, undo; or a custom icon from spec.icons'),
      variant: s.enumOf(BUTTON_VARIANTS, 'solid (default), soft, outline, ghost for quiet toolbar actions, link for inline text actions'),
      size: s.enumOf(SIZES, 'lg for touch screens and the one main action'),
      block: s.boolean('Full width, e.g. the main action on a phone'),
      iconOnly: s.boolean('Show only the icon; the label is read by screen readers and shown as a tooltip'),
      disabled: expr(),
      on: s.object({ click: actions() }, { required: ['click'] })
    }, ['label', 'on'], (v, _p, report) => {
      if (v.iconOnly && v.icon === undefined) report('icon', 'is required with iconOnly')
    }),
    menu: node('menu', {
      label: s.string({ max: 60, description: 'Button text, or the screen reader name with iconOnly' }),
      icon,
      iconOnly: s.boolean(),
      variant: s.enumOf(BUTTON_VARIANTS),
      size: s.enumOf(SIZES),
      items: s.array(s.object({
        label: dynamicText(60),
        icon,
        intent: buttonIntent,
        visible: expr(),
        disabled: expr(),
        on: s.object({ click: actions() }, { required: ['click'] })
      }, { required: ['label', 'on'] }), { min: 1, max: 12 })
    }, ['label', 'items']),
    datenav: node('datenav', {
      state: stateName,
      label: s.string({ max: 80, description: 'Read by screen readers' }),
      step: s.enumOf(['day', 'week', 'month'], 'How far previous and next move; default day'),
      today: s.boolean('Show a Today button; default true'),
      format: s.enumOf(['date_short', 'weekday', 'daymonth', 'date', 'monthyear'], 'How the chosen date reads next to the arrows; default the date picker alone'),
      on: s.object({ change: actions() }, { description: 'change runs after the date state is written; event.value is the ISO date' })
    }, ['state', 'label']),
    row: node('row', {
      gap: s.enumOf(GAPS),
      align: s.enumOf([...ALIGNS, 'baseline'], 'Cross-axis alignment; default center'),
      justify: s.enumOf(JUSTIFIES, 'between pushes the first and last child to the edges'),
      wrap: s.boolean('Wrap onto new lines when narrow; default true'),
      stackBelow: s.enumOf(BREAKPOINTS, 'Becomes a vertical stack below this width'),
      children: children(40)
    }, ['children']),
    sidebar: node('sidebar', {
      side: s.enumOf(['start', 'end'], 'Where the aside sits; default end (right)'),
      asideWidth: s.enumOf(ASIDE_WIDTHS, 'sm 240, md 300, lg 380 px'),
      gap: s.enumOf(GAPS),
      align: s.enumOf(ALIGNS),
      collapseBelow: s.enumOf([...BREAKPOINTS, 'never'], 'Stacks below this width; default md'),
      stackAside: s.enumOf(['first', 'last'], 'Where the aside goes once stacked; default last'),
      children: s.array(s.ref('Node'), { min: 2, max: 2, description: 'The main content, then the aside' })
    }, ['children']),
    grid: node('grid', {
      columns: s.anyOf([
        s.number({ min: 1, max: MAX_GRID_COLUMNS, integer: true }),
        s.literal('auto'),
        s.object({
          base: s.number({ min: 1, max: MAX_GRID_COLUMNS, integer: true }),
          sm: s.number({ min: 1, max: MAX_GRID_COLUMNS, integer: true }),
          md: s.number({ min: 1, max: MAX_GRID_COLUMNS, integer: true }),
          lg: s.number({ min: 1, max: MAX_GRID_COLUMNS, integer: true }),
          xl: s.number({ min: 1, max: MAX_GRID_COLUMNS, integer: true })
        }, { description: 'Columns per width, mobile first, e.g. {"base": 1, "md": 2, "lg": 4}' })
      ], 'A number narrows automatically on small widths; auto fits as many minWidth columns as there is room for; a map sets them per width'),
      minWidth: s.enumOf(MIN_WIDTHS),
      gap: s.enumOf(GAPS),
      align: s.enumOf(ALIGNS),
      children: children()
    }, ['children']),
    split: node('split', {
      ratio: s.enumOf(SPLIT_RATIOS),
      gap: s.enumOf(GAPS),
      align: s.enumOf(ALIGNS),
      collapseBelow: s.enumOf([...BREAKPOINTS, 'never'], 'Stacks below this width; default md'),
      children: s.array(s.ref('Node'), { min: 2, max: 2 })
    }, ['children']),
    card: node('card', {
      tone: dynEnum(CARD_TONES, 'surface (default), sunken, outline, accent, or an intent for status cards and banners'),
      pad: s.enumOf(PADS),
      gap: s.enumOf(GAPS),
      title: dynamicText(160),
      eyebrow: dynamicText(60),
      selected: expr(),
      open: open(),
      on: handlers({ click: 'runs when the card is clicked (the whole card is the target); nodes inside keep their own clicks' }),
      children: children(60)
    }, ['children']),
    scroll: node('scroll', {
      height: s.enumOf(SCROLL_HEIGHTS, 'Maximum height before it scrolls: sm 200, md 320, lg 480, xl 640 px'),
      children: children()
    }, ['children']),
    sticky: node('sticky', {
      edge: s.enumOf(['top', 'bottom'], 'Stays at the top (headers, filters) or bottom (save bars) while the tool scrolls'),
      children: children(20)
    }, ['children']),
    disclosure: node('disclosure', {
      title: dynamicText(160),
      summary: dynamicText(160),
      initiallyOpen: s.boolean(),
      children: children(60)
    }, ['title', 'children']),
    empty: node('empty', {
      title: dynamicText(160),
      text: dynamicText(600),
      icon,
      children: children(4)
    }, ['title']),
    list: node('list', {
      // Interfaces saved before Max Markup could list up to 50 static strings
      items: s.anyOf([expr(), s.array(s.string({ max: 500 }), { min: 1, max: 50 })]),
      as: s.string({ max: 32, pattern: SOURCE_ID_RE, description: 'Names the element: loop.<as> in the template and handlers inside it' }),
      children: children(30),
      ordered: s.boolean(),
      divided: s.boolean('Lines between items; default true'),
      density: s.enumOf(['compact', 'normal', 'roomy']),
      selected: expr(),
      open: open(),
      on: handlers({ select: 'runs when an item is clicked; item and loop.<as> are that item' }),
      empty: s.string({ max: 200 }),
      maxItems: s.number({ min: 1, max: 500, integer: true })
    }, ['items']),
    repeat: node('repeat', {
      items: expr(),
      as: s.string({ max: 32, pattern: SOURCE_ID_RE, description: 'Names the element: loop.<as> in the children and handlers inside them' }),
      children: s.array(s.ref('Node'), { min: 1, max: 20 }),
      empty: s.string({ max: 200 }),
      maxItems: s.number({ min: 1, max: 500, integer: true })
    }, ['items', 'children']),
    timeline: node('timeline', {
      items: expr(),
      as: s.string({ max: 32, pattern: SOURCE_ID_RE }),
      time: expr(),
      intent: expr(),
      children: s.array(s.ref('Node'), { min: 1, max: 20 }),
      empty: s.string({ max: 200 })
    }, ['items', 'children']),
    badge: node('badge', {
      text: dynamicText(80),
      intent,
      variant: s.enumOf(['soft', 'solid', 'outline']),
      size: s.enumOf(['sm', 'md']),
      mono: s.boolean(),
      icon
    }, ['text']),
    icon: node('icon', {
      name: icon,
      intent,
      size: s.enumOf(ICON_SIZES, 'sm, md and lg follow the text; xl is for larger pictograms'),
      label: s.string({ max: 80, description: 'Read by screen readers; leave out for decorative icons' })
    }, ['name']),
    avatar: node('avatar', {
      name: dynamicText(120),
      intent,
      size: s.enumOf(SIZES)
    }, ['name']),
    keyvalue: node('keyvalue', {
      items: s.array(s.object({
        label: dynamicText(80),
        value: expr(),
        format: s.enumOf(FMT_FORMATS),
        currency: s.string({ min: 3, max: 3, pattern: /^[A-Z]{3}$/ }),
        mono: s.boolean(),
        intent,
        visible: expr()
      }, { required: ['label', 'value'] }), { min: 1, max: 24 }),
      layout: s.enumOf(['stacked', 'inline', 'columns'], 'stacked: label above value; inline: label and value on one line; columns: a two-column list')
    }, ['items']),
    progress: node('progress', {
      value: expr(),
      max: expr(),
      label: dynamicText(80),
      intent,
      variant: s.enumOf(['bar', 'ring']),
      show: s.enumOf(['none', 'fraction', 'percent'], 'Text next to the bar: 3/7, 43 %, or nothing')
    }, ['value']),
    image: node('image', {
      source: s.enumOf(['product', 'file'], 'A product\'s main image, or a Johku file by id. URLs are not allowed'),
      id: expr(),
      alt: dynamicText(160),
      ratio: s.enumOf(['square', 'wide', 'portrait', 'auto'])
    }, ['source', 'id']),
    spacer: node('spacer', {
      size: s.enumOf(['xs', 'sm', 'md', 'lg', 'xl', 'fill'], 'fill pushes the following children of a row to the end')
    }),
    use: node('use', {
      component: s.string({ max: 32, pattern: SOURCE_ID_RE, description: 'Key in spec.components' }),
      with: s.record(expr(), { maxKeys: MAX_COMPONENT_PARAMS, keyPattern: PARAM_RE, description: 'Param values, each an expression evaluated where the use node is' }),
      children: children(40)
    }, ['component']),
    slot: node('slot', {}),
    datatable: node('datatable', {
      title: s.string({ max: 120 }),
      narrow: s.enumOf(['cards', 'scroll'], 'Below 480 px: cards shows each row as a stacked card (default with more than 4 columns), scroll keeps the table and scrolls sideways'),
      caption: s.string({ max: 300 }),
      rows: expr(),
      rowKey: s.string({ max: 64, pattern: COLUMN_KEY_RE, description: 'Row field that identifies a row; default id' }),
      columns: s.array(s.object({
        key: s.string({ max: 64, pattern: COLUMN_KEY_RE }),
        label: s.string({ max: 80 }),
        format: s.enumOf(VALUE_FORMATS),
        currency: s.string({ min: 3, max: 3, pattern: /^[A-Z]{3}$/ }),
        align: s.enumOf(['start', 'center', 'end']),
        width: s.enumOf(['auto', 'narrow', 'wide']),
        value: expr(),
        edit: s.object({
          state: stateName,
          input: s.enumOf(EDIT_INPUTS),
          min: s.number(),
          max: s.number(),
          step: timeStep,
          placeholder: s.string({ max: 40 }),
          options: options()
        }, { required: ['state', 'input'], description: 'Editable cell: the value is stored in a map state keyed by the row key. A time cell stores "HH:MM"; clearing it removes the key', refine: (v, p, report) => {
          timeRules(v, p, report)
          if (v.input !== 'time' && v.step !== undefined) report('step', 'is only used by time')
        } })
      }, { required: ['key', 'label'] }), { min: 1, max: 16 }),
      select: s.string({ max: 32, pattern: SOURCE_ID_RE, description: 'List state holding the selected row keys' }),
      open: s.object({
        to: s.enumOf(NAVIGATE_TARGETS),
        id: expr()
      }, { required: ['to'], description: 'Clicking a row opens the record in a POS details column, as in POS lists. id is evaluated per row and defaults to the row key. Use this instead of an "Open" row action' }),
      rowActions: s.array(s.object({
        label: s.string({ max: 40 }),
        intent: s.enumOf(['neutral', 'accent', 'positive', 'warning', 'negative']),
        visible: expr(),
        on: s.object({ click: actions() }, { required: ['click'] })
      }, { required: ['label', 'on'] }), { max: 4 }),
      empty: s.string({ max: 200 }),
      maxRows: s.number({ min: 1, max: 500, integer: true }),
      page: stateName,
      pageSize: s.number({ min: 1, max: 100, integer: true, description: 'Rows per page when page is set; default 50' }),
      sort: stateName,
      sortDir: stateName,
      count: expr(),
      tree: s.object({
        parentField: s.string({ max: 64, pattern: COLUMN_KEY_RE, description: 'Row field that holds the parent id, e.g. parent_id' })
      }, { required: ['parentField'] }),
      on: s.object({
        sort: actions(),
        page: actions()
      }, { description: 'sort runs after a header click writes sort/sortDir. page runs after the pager writes page' })
    }, ['rows', 'columns'], (v, _p, report) => {
      const keys = (v.columns as { key: string }[]).map(c => c.key)
      if (new Set(keys).size !== keys.length) report('columns', 'column keys must be unique')
    }),
    calendar: node('calendar', {
      kind: s.enumOf(CALENDAR_KINDS, 'showtimes, product_schedule, resource_schedule or agenda'),
      events: expr(),
      mode: s.enumOf(['daily', 'hourly'], 'Schedules only'),
      from: expr(),
      to: expr(),
      period: expr(),
      open: s.object({
        to: s.enumOf(NAVIGATE_TARGETS),
        id: expr()
      }, { required: ['to'], description: 'Clicking an event opens the record. id is evaluated per event and defaults to the event id' }),
      on: s.object({
        change: actions()
      }, { description: 'change runs after prev/next writes from/to. event.from and event.to are ISO dates' }),
      empty: s.string({ max: 200 })
    }, ['kind', 'events']),
    board: node('board', {
      source: s.enumOf(['orders'], 'orders; deliveries later'),
      columns: s.array(s.object({
        title: s.string({ max: 80 }),
        tag_id: s.number({ min: 1, integer: true }),
        status: s.enumOf(BOARD_STATUSES),
        rows: expr()
      }, { required: ['title', 'rows'] }), { min: 1, max: 6 }),
      open: s.object({
        to: s.enumOf(NAVIGATE_TARGETS),
        id: expr()
      }, { required: ['to'], description: 'Clicking a card opens the order' }),
      on: s.object({
        move: actions()
      }, { description: 'move runs after a drag or column button. event.id, event.add_tags, event.remove_tags, event.processed' }),
      empty: s.string({ max: 200 })
    }, ['source', 'columns']),
    detail: node('detail', {
      row: expr(),
      title: s.string({ max: 120 }),
      empty: s.string({ max: 200 }),
      fields: s.array(s.object({
        key: s.string({ max: 64, pattern: COLUMN_KEY_RE }),
        label: s.string({ max: 80 }),
        format: s.enumOf(VALUE_FORMATS),
        currency: s.string({ min: 3, max: 3, pattern: /^[A-Z]{3}$/ })
      }, { required: ['key', 'label'] }), { min: 1, max: 24 }),
      open: s.object({
        to: s.enumOf(NAVIGATE_TARGETS),
        id: expr()
      }, { required: ['to'] })
    }, ['row', 'fields']),
    table: node('table', {
      title: s.string({ max: 120 }),
      caption: s.string({ max: 300 }),
      columns: s.array(s.object({
        key: s.string({ max: 64, pattern: COLUMN_KEY_RE }),
        label: s.string({ max: 80 }),
        format: s.enumOf(VALUE_FORMATS),
        currency: s.string({ min: 3, max: 3, pattern: /^[A-Z]{3}$/ }),
        align: s.enumOf(['start', 'center', 'end']),
        width: s.enumOf(['auto', 'narrow', 'wide'])
      }, { required: ['key', 'label'] }), { min: 1, max: 24 }),
      rows: s.anyOf([
        s.array(s.record(scalar, { maxKeys: 24, keyPattern: COLUMN_KEY_RE }), { max: 2000 }),
        computed()
      ], 'Static rows or {expr} that evaluates to a list of objects'),
      maxRows: s.number({ min: 1, max: 500, integer: true })
    }, ['columns', 'rows']),
    chart: node('chart', {
      title: s.string({ max: 120 }),
      caption: s.string({ max: 300 }),
      height: s.enumOf(['sm', 'md', 'lg']),
      chart: s.anyOf([
        s.ref('Chart'),
        s.object({
          kind: s.enumOf(['bar', 'line', 'area']),
          data: computed(),
          category: s.string({ max: 64, pattern: COLUMN_KEY_RE }),
          series: s.array(s.object({
            field: s.string({ max: 64, pattern: COLUMN_KEY_RE }),
            name: s.string({ max: 80 })
          }, { required: ['field', 'name'] }), { min: 1, max: 12 }),
          format: s.enumOf(VALUE_FORMATS),
          currency: s.string({ min: 3, max: 3, pattern: /^[A-Z]{3}$/ }),
          horizontal: s.boolean()
        }, { required: ['kind', 'data', 'category', 'series'] })
      ])
    }, ['chart'])
  }
}

const stateDefSchema = s.object({
  type: s.enumOf(STATE_TYPES),
  initial: s.anyOf([scalar, s.array(scalar, { max: 200 }), s.record(scalar, { maxKeys: 200, keyPattern: MAP_KEY_RE })])
}, {
  required: ['type'],
  refine: (v, _p, report) => {
    if (v.initial === undefined) return
    const i = v.initial
    const ok = v.type === 'list' ? Array.isArray(i)
      : v.type === 'map' || v.type === 'record' ? !!i && typeof i === 'object' && !Array.isArray(i)
        : v.type === 'number' ? typeof i === 'number' || i === null
          : v.type === 'boolean' ? typeof i === 'boolean'
            : typeof i === 'string'
    if (!ok) report('initial', `does not match type ${String(v.type)}`)
  }
})

let cached: s.Validator | null = null

export function interfaceValidator (): s.Validator {
  if (cached) return cached
  const root = s.object({
    version: s.literal(SPEC_VERSION),
    kind: s.literal(INTERFACE_KIND),
    title: s.string({ min: 1, max: 160 }),
    description: s.string({ max: 600 }),
    locale: s.string({ max: 5, pattern: LOCALE_RE, description: 'Language of the labels and text, e.g. fi_FI' }),
    preset: s.enumOf(presetIds(), PRESET_DESCRIPTION),
    capabilities: s.array(s.enumOf(CAPABILITIES), { max: CAPABILITIES.length, description: 'Capabilities of every declared operation' }),
    state: s.record(stateDefSchema, { maxKeys: MAX_STATE, keyPattern: SOURCE_ID_RE }),
    operations: s.record(s.object({
      operation: s.enumOf(Object.keys(OPERATIONS), 'Catalog operation'),
      label: s.string({ max: 80, description: 'What it does here, in the interface language; shown to the approver' }),
      confirmAbove: s.number({ min: 0, max: 200, integer: true, description: 'Writes only. Once the tool is approved, a call that changes more items than this asks the user to confirm first; 0 always asks. Leave out to save without asking. While the tool is being tried out before approval, every write asks.' })
    }, { required: ['operation'] }), { maxKeys: MAX_OPERATIONS, keyPattern: SOURCE_ID_RE }),
    metadataNamespaces: s.array(s.string({ max: 32, pattern: METADATA_NS_RE }), { max: MAX_METADATA_NAMESPACES, description: 'Namespaces this tool may stamp; shared shop-wide, not owned by the spec' }),
    derive: s.record(expr(), {
      maxKeys: MAX_DERIVE,
      keyPattern: SOURCE_ID_RE,
      description: `Named values computed in order from state and the derives above them, read as derived.<name> anywhere. Use them instead of repeating an expression. At most ${MAX_DERIVE}; they are recomputed on every render and share ${MAX_DERIVE_STEPS} evaluation steps per render (one expression may use at most ${MAX_EXPR_STEPS}), and once those are used up the rest are null`
    }),
    components: s.record(s.object({
      params: s.array(s.string({ max: 32, pattern: PARAM_RE }), { max: MAX_COMPONENT_PARAMS, description: 'Names the body reads as props.<name>' }),
      body: s.array(s.ref('Node'), { min: 1, max: 40 })
    }, { required: ['body'] }), {
      maxKeys: MAX_COMPONENTS,
      keyPattern: SOURCE_ID_RE,
      description: 'Reusable templates rendered by { "type": "use", "component", "with" }. A slot node in the body renders the children of the use node'
    }),
    panels: s.record(s.object({
      title: dynamicText(120),
      size: s.enumOf(SIZES, 'Drawer width: sm 360, md 480, lg 720 px; full screen on phones'),
      body: s.array(s.ref('Node'), { min: 1, max: 60 })
    }, { required: ['title', 'body'] }), {
      maxKeys: MAX_PANELS,
      keyPattern: SOURCE_ID_RE,
      description: 'Drawers over the tool, opened with openPanel and closed with closePanel or Esc. Set the state they show before opening'
    }),
    metadataRead: s.enumOf(['declared', 'all'], 'declared (default) reads only the metadataNamespaces; all reads every namespace (shown to the approver). Writes are always limited to metadataNamespaces'),
    icons: s.record(s.object({
      path: s.string({ max: MAX_ICON_PATH, pattern: ICON_PATH_RE, description: 'SVG path data (the d attribute): M, L, H, V, C, S, Q, T, A and Z commands with numbers' }),
      viewBox: s.string({ max: 40, pattern: ICON_VIEWBOX_RE, description: 'The coordinate box the path is drawn in; default 0 0 24 24' }),
      style: s.enumOf(['stroke', 'fill'], 'stroke (default) draws outlines like the built-in icons; fill fills the shapes, as in most icon sets')
    }, { required: ['path'] }), {
      maxKeys: MAX_CUSTOM_ICONS,
      keyPattern: CUSTOM_ICON_RE,
      description: 'The tool\'s own icons by name, used like built-in ones. They take the text colour and the icon sizes'
    }),
    on: s.object({ load: actions() }, { description: 'load runs when the interface opens, on refresh, and in the background after another tool or device saves a change to the record types it reads' }),
    refresh: s.number({
      min: REFRESH_MIN_SECONDS,
      max: REFRESH_MAX_SECONDS,
      integer: true,
      description: `Seconds between background reruns of on.load, ${REFRESH_MIN_SECONDS}–${REFRESH_MAX_SECONDS}, for data that changes outside Johku Max tools (orders, check-ins at the desk). POS waits while the tool is hidden, a panel is open or a field has focus, spreads the time ±10% and runs at most once per ${BACKGROUND_WINDOW_SECONDS} s. Changes saved by Max tools reload open tools without it`
    }),
    body: s.array(s.ref('Node'), { min: 1, max: 200 })
  }, { required: ['version', 'kind', 'title', 'locale', 'capabilities', 'body'] })
  cached = {
    root,
    defs: {
      Node: s.oneOfBy('type', interfaceNodeVariants(), 'Interface node'),
      Action: s.oneOfBy('do', actionVariants(), 'Action run in order when an event fires'),
      Expr: exprSchema()
    },
    maxDepth: INTERFACE_SCHEMA_DEPTH
  }
  return cached
}

export function interfaceJsonSchema (): Record<string, unknown> {
  return {
    ...s.toJsonSchema(interfaceValidator(), { id: 'https://schemas.johku.com/max/interface.v1.json', title: 'Johku Max interface' }),
    'x-max-bytes': MAX_SPEC_BYTES,
    'x-max-nodes': MAX_SPEC_NODES,
    'x-max-node-depth': MAX_DEPTH
  }
}

// ---------------------------------------------------------------------------
// Walking

/** Scopes in effect at a node: named loop items, whether a template element is in scope, and component params */
export interface WalkCtx { loops: string[], template: boolean, component: string | null, params: string[] | null }

const ROOT_CTX: WalkCtx = { loops: [], template: false, component: null, params: null }

type Ctx = { row?: boolean, result?: boolean, error?: boolean, event?: boolean, walk?: WalkCtx, derive?: string[] }

export interface InterfaceNodeVisit { node: Record<string, unknown>, path: string, depth: number, ctx: WalkCtx }

/** Every node with children: layout containers, interface containers, templates and use (its slot content) */
export function nodeChildren (node: Record<string, unknown>): unknown[] | null {
  return Array.isArray(node.children) ? node.children : null
}

export function walkInterfaceNodes (nodes: unknown, visit: (v: InterfaceNodeVisit) => void, path = 'body', depth = 1, ctx: WalkCtx = ROOT_CTX): void {
  if (!Array.isArray(nodes) || depth > 64) return
  nodes.forEach((node, i) => {
    if (!node || typeof node !== 'object') return
    const n = node as Record<string, unknown>
    const p = `${path}[${i}]`
    visit({ node: n, path: p, depth, ctx })
    const kids = nodeChildren(n)
    if (!kids) return
    const inner = (TEMPLATE_TYPES as readonly string[]).includes(String(n.type))
      ? { ...ctx, template: true, loops: typeof n.as === 'string' ? [...ctx.loops, n.as] : ctx.loops }
      : ctx
    walkInterfaceNodes(kids, visit, `${p}.children`, depth + 1, inner)
  })
}

/** The body, every component body and every panel body, each with its own scope */
export function walkInterfaceSpec (spec: MaxInterfaceSpec, visit: (v: InterfaceNodeVisit) => void): void {
  walkInterfaceNodes(spec.body, visit)
  for (const [name, def] of Object.entries(spec.components ?? {})) {
    walkInterfaceNodes(def?.body, visit, `components.${name}.body`, 1, { ...ROOT_CTX, component: name, params: def?.params ?? [] })
  }
  for (const [name, def] of Object.entries(spec.panels ?? {})) {
    walkInterfaceNodes(def?.body, visit, `panels.${name}.body`, 1)
  }
}

interface HandlerVisit { actions: Action[], path: string, ctx: Ctx, origin: string, nesting: number }
interface ExprVisit { expr: unknown, path: string, ctx: Ctx }

const isComputed = (v: unknown): v is { expr: unknown } => !!v && typeof v === 'object' && !Array.isArray(v) && 'expr' in v

function nodeOrigin (node: Record<string, unknown>): string {
  const label = typeof node.label === 'string' ? node.label : typeof node.title === 'string' ? node.title : null
  return label ? `${node.type}: ${label}` : String(node.type)
}

const on = (node: Record<string, unknown>, event: string): unknown => (node.on as Record<string, unknown> | undefined)?.[event]

/** Every handler (with nested then/catch) and every expression, with the scopes it may read. */
export function collectInterface (spec: MaxInterfaceSpec): { handlers: HandlerVisit[], exprs: ExprVisit[] } {
  const handlers: HandlerVisit[] = []
  const exprs: ExprVisit[] = []
  const addExpr = (e: unknown, path: string, ctx: Ctx) => { if (e !== undefined) exprs.push({ expr: e, path, ctx }) }
  const addText = (v: unknown, path: string, ctx: Ctx) => { if (isComputed(v)) addExpr(v.expr, `${path}.expr`, ctx) }

  const addHandler = (list: unknown, path: string, ctx: Ctx, origin: string, nesting = 0) => {
    if (!Array.isArray(list)) return
    handlers.push({ actions: list as Action[], path, ctx, origin, nesting })
    ;(list as Action[]).forEach((a, i) => {
      const p = `${path}[${i}]`
      if (!a || typeof a !== 'object') return
      addExpr(a.when, `${p}.when`, ctx)
      switch (a.do) {
        case 'setState': addExpr(a.value, `${p}.value`, ctx); break
        case 'setIn': addExpr(a.key, `${p}.key`, ctx); addExpr(a.value, `${p}.value`, ctx); break
        case 'callOperation':
          for (const [k, v] of Object.entries((a.input as Record<string, unknown> | undefined) ?? {})) addExpr(v, `${p}.input.${k}`, ctx)
          addHandler(a.then, `${p}.then`, { ...ctx, result: true }, origin, nesting + 1)
          addHandler(a.catch, `${p}.catch`, { ...ctx, error: true }, origin, nesting + 1)
          break
        case 'openConfirm':
          addText(a.message, `${p}.message`, ctx)
          addHandler(a.then, `${p}.then`, ctx, origin, nesting + 1)
          break
        case 'navigate': addExpr(a.id, `${p}.id`, ctx); break
        case 'toast': addText(a.message, `${p}.message`, ctx); break
        case 'print': addExpr(a.id, `${p}.id`, ctx); break
        case 'copy': addExpr(a.value, `${p}.value`, ctx); break
      }
    })
  }

  addHandler(spec.on?.load, 'on.load', {}, 'load')
  const derives = Object.entries(spec.derive ?? {})
  derives.forEach(([name, e], i) => addExpr(e, `derive.${name}`, { derive: derives.slice(0, i).map(([n]) => n) }))
  for (const [name, def] of Object.entries(spec.panels ?? {})) addText(def?.title, `panels.${name}.title`, {})

  walkInterfaceSpec(spec, ({ node, path, ctx: walk }) => {
    const base: Ctx = { walk }
    const inner: Ctx = { walk: { ...walk, template: true, loops: typeof node.as === 'string' ? [...walk.loops, node.as] : walk.loops } }
    addExpr(node.visible, `${path}.visible`, base)
    for (const prop of ['intent', 'icon', 'tone', ...(node.type === 'icon' ? ['name'] : [])]) addText(node[prop], `${path}.${prop}`, base)
    switch (node.type) {
      case 'heading': addText(node.text, `${path}.text`, base); addText(node.eyebrow, `${path}.eyebrow`, base); break
      case 'text':
        addText(node.text, `${path}.text`, base)
        ;((node.spans as Record<string, unknown>[] | undefined) ?? []).forEach((span, i) => {
          addText(span?.text, `${path}.spans[${i}].text`, base)
          addText(span?.intent, `${path}.spans[${i}].intent`, base)
        })
        break
      case 'callout': addText(node.text, `${path}.text`, base); break
      case 'metric': addText(node.value, `${path}.value`, base); addText(node.caption, `${path}.caption`, base); break
      case 'badge': addText(node.text, `${path}.text`, base); break
      case 'avatar': addText(node.name, `${path}.name`, base); break
      case 'empty': addText(node.title, `${path}.title`, base); addText(node.text, `${path}.text`, base); break
      case 'disclosure': addText(node.title, `${path}.title`, base); addText(node.summary, `${path}.summary`, base); break
      case 'image': addExpr(node.id, `${path}.id`, base); addText(node.alt, `${path}.alt`, base); break
      case 'progress':
        addExpr(node.value, `${path}.value`, base)
        addExpr(node.max, `${path}.max`, base)
        addText(node.label, `${path}.label`, base)
        break
      case 'keyvalue':
        ;((node.items as Record<string, unknown>[] | undefined) ?? []).forEach((item, i) => {
          addText(item?.label, `${path}.items[${i}].label`, base)
          addExpr(item?.value, `${path}.items[${i}].value`, base)
          addExpr(item?.visible, `${path}.items[${i}].visible`, base)
          addText(item?.intent, `${path}.items[${i}].intent`, base)
        })
        break
      case 'card':
        addText(node.title, `${path}.title`, base)
        addText(node.eyebrow, `${path}.eyebrow`, base)
        addExpr(node.selected, `${path}.selected`, base)
        addExpr((node.open as { id?: unknown } | undefined)?.id, `${path}.open.id`, base)
        addHandler(on(node, 'click'), `${path}.on.click`, base, nodeOrigin(node))
        break
      case 'repeat': addExpr(node.items, `${path}.items`, base); break
      case 'list':
        addExpr(node.items, `${path}.items`, base)
        addExpr(node.selected, `${path}.selected`, inner)
        addExpr((node.open as { id?: unknown } | undefined)?.id, `${path}.open.id`, inner)
        addHandler(on(node, 'select'), `${path}.on.select`, inner, nodeOrigin(node))
        break
      case 'timeline':
        addExpr(node.items, `${path}.items`, base)
        addExpr(node.time, `${path}.time`, inner)
        addExpr(node.intent, `${path}.intent`, inner)
        break
      case 'use':
        for (const [k, v] of Object.entries((node.with as Record<string, unknown> | undefined) ?? {})) addExpr(v, `${path}.with.${k}`, base)
        break
      case 'field':
        addExpr(node.key, `${path}.key`, base)
        addExpr(node.disabled, `${path}.disabled`, base)
        addText(node.options, `${path}.options`, base)
        addHandler(on(node, 'change'), `${path}.on.change`, { ...base, event: true }, nodeOrigin(node))
        break
      case 'datenav':
        addHandler(on(node, 'change'), `${path}.on.change`, { ...base, event: true }, nodeOrigin(node))
        break
      case 'button':
        addText(node.label, `${path}.label`, base)
        addExpr(node.disabled, `${path}.disabled`, base)
        addHandler(on(node, 'click'), `${path}.on.click`, base, nodeOrigin(node))
        break
      case 'menu':
        ;((node.items as Record<string, unknown>[] | undefined) ?? []).forEach((item, i) => {
          addText(item?.label, `${path}.items[${i}].label`, base)
          addExpr(item?.visible, `${path}.items[${i}].visible`, base)
          addExpr(item?.disabled, `${path}.items[${i}].disabled`, base)
          addText(item?.intent, `${path}.items[${i}].intent`, base)
          addText(item?.icon, `${path}.items[${i}].icon`, base)
          addHandler(on(item ?? {}, 'click'), `${path}.items[${i}].on.click`, base, `${nodeOrigin(node)} / ${String(typeof item?.label === 'string' ? item.label : i)}`)
        })
        break
      case 'filterbar':
        ;((node.filters as Record<string, unknown>[] | undefined) ?? []).forEach((f, i) => {
          addText(f.options, `${path}.filters[${i}].options`, base)
          addHandler(on(f, 'change'), `${path}.filters[${i}].on.change`, { ...base, event: true }, `filter: ${String(f.label)}`)
        })
        break
      case 'datatable': {
        const row: Ctx = { ...base, row: true }
        addExpr(node.rows, `${path}.rows`, base)
        addExpr(node.count, `${path}.count`, base)
        ;((node.columns as Record<string, unknown>[] | undefined) ?? []).forEach((c, i) => addExpr(c.value, `${path}.columns[${i}].value`, row))
        addExpr((node.open as { id?: unknown } | undefined)?.id, `${path}.open.id`, row)
        addHandler(on(node, 'sort'), `${path}.on.sort`, base, nodeOrigin(node))
        addHandler(on(node, 'page'), `${path}.on.page`, base, nodeOrigin(node))
        ;((node.rowActions as Record<string, unknown>[] | undefined) ?? []).forEach((ra, i) => {
          addExpr(ra.visible, `${path}.rowActions[${i}].visible`, row)
          addHandler(on(ra, 'click'), `${path}.rowActions[${i}].on.click`, row, `${nodeOrigin(node)} / ${String(ra.label)}`)
        })
        break
      }
      case 'calendar':
        addExpr(node.events, `${path}.events`, base)
        addExpr(node.from, `${path}.from`, base)
        addExpr(node.to, `${path}.to`, base)
        addExpr(node.period, `${path}.period`, base)
        addExpr((node.open as { id?: unknown } | undefined)?.id, `${path}.open.id`, { ...base, row: true })
        addHandler(on(node, 'change'), `${path}.on.change`, { ...base, event: true }, nodeOrigin(node))
        break
      case 'board':
        ;((node.columns as Record<string, unknown>[] | undefined) ?? []).forEach((c, i) => addExpr(c.rows, `${path}.columns[${i}].rows`, base))
        addExpr((node.open as { id?: unknown } | undefined)?.id, `${path}.open.id`, { ...base, row: true })
        addHandler(on(node, 'move'), `${path}.on.move`, { ...base, event: true }, nodeOrigin(node))
        break
      case 'detail':
        addExpr(node.row, `${path}.row`, base)
        addExpr((node.open as { id?: unknown } | undefined)?.id, `${path}.open.id`, { ...base, row: true })
        break
      case 'table':
        if (isComputed(node.rows)) addExpr(node.rows.expr, `${path}.rows.expr`, base)
        break
      case 'chart': {
        const chart = node.chart as { data?: unknown } | undefined
        if (isComputed(chart?.data)) addExpr(chart.data.expr, `${path}.chart.data.expr`, base)
        break
      }
    }
  })
  return { handlers, exprs }
}

/** The state type each field input stores */
export const FIELD_STATE: Record<string, StateType[]> = {
  toggle: ['boolean'],
  checkbox: ['boolean'],
  number: ['number'],
  stepper: ['number'],
  slider: ['number'],
  select: ['string', 'number'],
  chips: ['string', 'number'],
  segmented: ['string', 'number'],
  radio: ['string', 'number'],
  multiselect: ['list'],
  checkboxes: ['list']
}

/** State references outside expressions: controls and actions that read or write a named state */
function stateUses (spec: MaxInterfaceSpec, handlers: HandlerVisit[]): { name: string, path: string, need?: StateType[] }[] {
  const out: { name: string, path: string, need?: StateType[] }[] = []
  walkInterfaceSpec(spec, ({ node, path }) => {
    if (node.type === 'field' && typeof node.state === 'string') {
      // With key the field edits one entry of a map, e.g. per departure inside a repeat
      out.push({ name: node.state, path: `${path}.state`, need: node.key !== undefined ? ['map'] : FIELD_STATE[String(node.input)] ?? ['string'] })
    }
    if (node.type === 'datenav' && typeof node.state === 'string') {
      out.push({ name: node.state, path: `${path}.state`, need: ['string'] })
    }
    if (node.type === 'filterbar') {
      ;((node.filters as Record<string, unknown>[] | undefined) ?? []).forEach((f, i) => {
        if (typeof f.state === 'string') out.push({ name: f.state, path: `${path}.filters[${i}].state`, need: f.input === 'select' ? ['string', 'number'] : f.input === 'multiselect' ? ['list'] : ['string'] })
        if (typeof f.period === 'string') out.push({ name: f.period, path: `${path}.filters[${i}].period`, need: ['string'] })
        if (typeof f.from === 'string') out.push({ name: f.from, path: `${path}.filters[${i}].from`, need: ['string'] })
        if (typeof f.to === 'string') out.push({ name: f.to, path: `${path}.filters[${i}].to`, need: ['string'] })
      })
    }
    if (node.type === 'datatable') {
      if (typeof node.select === 'string') out.push({ name: node.select, path: `${path}.select`, need: ['list'] })
      if (typeof node.page === 'string') out.push({ name: node.page, path: `${path}.page`, need: ['number'] })
      if (typeof node.sort === 'string') out.push({ name: node.sort, path: `${path}.sort`, need: ['string'] })
      if (typeof node.sortDir === 'string') out.push({ name: node.sortDir, path: `${path}.sortDir`, need: ['string'] })
      ;((node.columns as Record<string, unknown>[] | undefined) ?? []).forEach((c, i) => {
        const edit = c.edit as { state?: unknown } | undefined
        if (edit && typeof edit.state === 'string') out.push({ name: edit.state, path: `${path}.columns[${i}].edit.state`, need: ['map'] })
      })
    }
  })
  for (const h of handlers) {
    h.actions.forEach((a, i) => {
      const p = `${h.path}[${i}]`
      if ((a.do === 'setState' || a.do === 'setIn') && typeof a.state === 'string') out.push({ name: a.state, path: `${p}.state`, need: a.do === 'setIn' ? ['map'] : undefined })
      if (a.do === 'callOperation' && typeof a.into === 'string') out.push({ name: a.into, path: `${p}.into` })
    })
  }
  return out
}

// ---------------------------------------------------------------------------
// Analysis, lint, description

/** Catalog lists that shops grow past one page. A first page in a select or unpaged table is not a POS list. */
export const BROWSE_LIST_OPS = new Set([
  'products.list', 'products.search', 'resources.list', 'customers.list', 'billingcustomers.list',
  'orders.list', 'bills.list', 'reservations.list', 'discounts.list', 'content.list', 'vouchers.list',
  'campaigns.list', 'subscriptions.list', 'shows.list', 'issues.list', 'categories.list'
])

/** One picker or table page of catalog matches. */
export const PICKER_LIMIT = 50

/** The list state a select's options expression maps, if it maps exactly one. */
export function optionsListState (options: unknown): string | null {
  if (options == null) return null
  const expr = options && typeof options === 'object' && 'expr' in options ? (options as { expr: unknown }).expr : options
  for (const ref of exprVars(expr)) {
    const m = /^state\.([A-Za-z][A-Za-z0-9_]*)$/.exec(ref.path)
    if (m) return m[1] ?? null
  }
  return null
}

/** The declared list operation that fills a catalog state, so the host can search it. */
export function listFillAction (spec: MaxInterfaceSpec, stateName: string): { operation: string, input: Record<string, unknown> } | null {
  const { handlers } = collectInterface(spec)
  let found: { operation: string, input: Record<string, unknown>, load: boolean } | null = null
  for (const h of handlers) {
    for (const a of h.actions) {
      if (a.do !== 'callOperation' || a.into !== stateName) continue
      const catalog = spec.operations?.[String(a.operation)]?.operation
      if (typeof catalog !== 'string' || !BROWSE_LIST_OPS.has(catalog)) continue
      if (!OPERATIONS[catalog]?.input?.query) continue
      const row = { operation: String(a.operation), input: (a.input as Record<string, unknown>) ?? {}, load: h.path === 'on.load' }
      if (!found || row.load) found = row
    }
  }
  return found ? { operation: found.operation, input: found.input } : null
}

function repeatOpensRecords (node: Record<string, unknown>): boolean {
  const walk = (nodes: unknown): boolean => {
    if (!Array.isArray(nodes)) return false
    return nodes.some(raw => {
      if (!raw || typeof raw !== 'object') return false
      const child = raw as { type?: unknown, on?: { click?: unknown }, children?: unknown }
      if (child.type === 'button') {
        const click = child.on?.click
        return Array.isArray(click) && click.length === 1 && (click[0] as { do?: unknown } | undefined)?.do === 'navigate'
      }
      if (child.type === 'stack' || child.type === 'grid') return walk(child.children)
      return false
    })
  }
  return walk(node.children)
}

function browseListStates (doc: MaxInterfaceSpec, handlers: HandlerVisit[]): Map<string, string> {
  const filled = new Map<string, string>()
  for (const h of handlers) {
    for (const a of h.actions) {
      if (a.do !== 'callOperation' || typeof a.into !== 'string') continue
      const op = doc.operations?.[String(a.operation)]?.operation
      if (typeof op === 'string' && BROWSE_LIST_OPS.has(op)) filled.set(a.into, op)
    }
  }
  return filled
}

function searchReloadsList (doc: MaxInterfaceSpec, stateName: string): boolean {
  let ok = false
  walkInterfaceSpec(doc, ({ node }) => {
    if (node.type === 'field' && node.input === 'search') {
      const change = (node.on as { change?: unknown } | undefined)?.change
      if (Array.isArray(change) && change.some(a => a && a.do === 'callOperation' && a.into === stateName)) ok = true
    }
    if (node.type === 'filterbar') {
      for (const f of (node.filters as { input?: unknown, on?: { change?: unknown } }[] | undefined) ?? []) {
        if (f.input !== 'search') continue
        const change = f.on?.change
        if (Array.isArray(change) && change.some(a => a && a.do === 'callOperation' && a.into === stateName)) ok = true
      }
    }
  })
  return ok
}

function mentionsState (expr: unknown, name: string): boolean {
  return exprVars(expr).some(ref => ref.path === `state.${name}`)
}

function lintCatalogBrowse (doc: MaxInterfaceSpec, handlers: HandlerVisit[], warnings: s.Issue[]): void {
  const filled = browseListStates(doc, handlers)
  if (!filled.size) return
  walkInterfaceSpec(doc, ({ node, path }) => {
    if (node.type === 'repeat' || node.type === 'list') {
      for (const [name, op] of filled) {
        if (!mentionsState(node.items, name) || !repeatOpensRecords(node)) continue
        warnings.push({
          path,
          message: `POS lists ${op} as a datatable; click the row (datatable.open), do not repeat Open buttons (design: convention)`
        })
      }
    }
    if (node.type !== 'datatable') return
    for (const [name, op] of filled) {
      if (!mentionsState(node.rows, name)) continue
      const paged = typeof node.page === 'string' && typeof node.pageSize === 'number' && node.count !== undefined &&
        Array.isArray((node.on as { page?: unknown } | undefined)?.page)
      if (!paged) {
        warnings.push({
          path,
          message: `POS lists page through every ${op} match; set page, pageSize, count and on.page and pass offset (design: convention)`
        })
      }
      if (OPERATIONS[op]?.input?.query && !searchReloadsList(doc, name)) {
        warnings.push({
          path,
          message: `POS lists have a search in the filterbar that reloads ${op} (design: convention)`
        })
      }
    }
  })
}

export function analyzeInterface (spec: MaxInterfaceSpec): SpecAnalysis {
  const required = new Set<Capability>()
  let opLevel = 3
  let personalData = false
  for (const ref of Object.values(spec.operations ?? {})) {
    const def = OPERATIONS[ref.operation]
    if (!def) continue
    if (ref.operation !== 'export.xlsx' && (!def.capabilityByInput || def.capabilityByInput.list)) required.add(def.capability)
    if (def.personal) personalData = true
    opLevel = Math.max(opLevel, def.level)
  }
  const { handlers } = collectInterface(spec)
  for (const h of handlers) {
    for (const a of h.actions) {
      if (a.do !== 'callOperation') continue
      const ref = spec.operations?.[String(a.operation)]
      // Metadata read with list rows needs the same capability as metadata.get
      if ((a.input as { with_metadata?: unknown } | undefined)?.with_metadata !== undefined) required.add('read:metadata')
      const def = ref ? OPERATIONS[ref.operation] : undefined
      if (def?.capabilityByInput) {
        for (const cap of operationCapabilities(def, a.input, spec.capabilities ?? [])) required.add(cap)
      }
      if (ref?.operation !== 'export.xlsx') continue
      const resource = (a.input as { resource?: unknown } | undefined)?.resource
      if (typeof resource !== 'string') continue
      const cap = exportCapability(resource)
      if (cap) required.add(cap)
      if (exportPersonal(resource)) personalData = true
    }
  }
  if (spec.metadataRead === 'all') required.add('read:metadata')
  const requiredCapabilities = CAPABILITIES.filter(c => required.has(c))
  const risks = requiredCapabilities.map(c => CAPABILITY_RISK[c])
  const risk: Risk = risks.includes('write') ? 'write' : risks.includes('egress') ? 'egress' : 'read'
  const minLevel = requiredCapabilities.reduce((lvl, c) => Math.max(lvl, CAPABILITY_LEVELS[c]), opLevel)
  const viewLevel = requiredCapabilities.filter(c => CAPABILITY_RISK[c] === 'read').reduce((lvl, c) => Math.max(lvl, CAPABILITY_LEVELS[c]), 3)
  let usesConditions = false
  walkInterfaceSpec(spec, ({ node }) => { if (node.visible !== undefined) usesConditions = true })
  return { requiredCapabilities, risk, personalData, minLevel, viewLevel, bindings: [], usesRules: false, usesConditions }
}

/** Every literal icon name a node uses, with where it is */
function iconUses (node: Record<string, unknown>): [string, string][] {
  const out: [string, string][] = []
  if (typeof node.icon === 'string') out.push(['icon', node.icon])
  if (node.type === 'icon' && typeof node.name === 'string') out.push(['name', node.name])
  if (Array.isArray(node.items)) {
    node.items.forEach((item, i) => {
      if (item && typeof item === 'object' && typeof (item as { icon?: unknown }).icon === 'string') out.push([`items[${i}].icon`, (item as { icon: string }).icon])
    })
  }
  return out
}

/** Literal metadata namespaces an operation call reads or writes */
export function metadataNamespaceUses (operation: string, input: Record<string, unknown>, path: string): { path: string, ns: string, mode: 'read' | 'write' }[] {
  const out: { path: string, ns: string, mode: 'read' | 'write' }[] = []
  for (const key of ['ns', 'meta_ns', 'meta_sort_ns']) {
    if (typeof input[key] === 'string') out.push({ path: `${path}.input.${key}`, ns: input[key] as string, mode: 'read' })
  }
  if (typeof input.with_metadata === 'string' && input.with_metadata !== '*') {
    for (const ns of input.with_metadata.split(',')) out.push({ path: `${path}.input.with_metadata`, ns, mode: 'read' })
  }
  if (operation === 'metadata.set' && Array.isArray(input.changes)) {
    input.changes.forEach((change, i) => {
      if (change && typeof change === 'object' && typeof (change as { ns?: unknown }).ns === 'string') {
        out.push({ path: `${path}.input.changes[${i}].ns`, ns: (change as { ns: string }).ns, mode: 'write' })
      }
    })
  }
  return out
}

/**
 * Nodes once every use is replaced by its component body, which is what renders (templates
 * count once). Reports component cycles and nesting beyond MAX_COMPONENT_NESTING.
 */
function expandedSize (doc: MaxInterfaceSpec, issues: s.Issue[]): number {
  const components = doc.components ?? {}
  const memo = new Map<string, number>()
  const reported = new Set<string>()
  const sizeOf = (nodes: unknown, chain: string[]): number => {
    if (!Array.isArray(nodes)) return 0
    let n = 0
    for (const raw of nodes) {
      if (!raw || typeof raw !== 'object') continue
      const node = raw as Record<string, unknown>
      n++
      n += sizeOf(node.children, chain)
      if (node.type === 'use' && typeof node.component === 'string' && components[node.component]) n += componentSize(node.component, chain)
    }
    return n
  }
  const componentSize = (name: string, chain: string[]): number => {
    if (chain.includes(name)) {
      const key = [...chain, name].join(' → ')
      if (!reported.has(key)) { reported.add(key); issues.push({ path: `components.${name}`, message: `components use each other in a loop: ${key}` }) }
      return 0
    }
    if (chain.length >= MAX_COMPONENT_NESTING) {
      if (!reported.has(name)) { reported.add(name); issues.push({ path: `components.${name}`, message: `components nest more than ${MAX_COMPONENT_NESTING} levels deep` }) }
      return 0
    }
    if (memo.has(name)) return memo.get(name)!
    const size = sizeOf(components[name]!.body, [...chain, name])
    memo.set(name, size)
    return size
  }
  let total = sizeOf(doc.body, [])
  for (const panel of Object.values(doc.panels ?? {})) total += sizeOf(panel?.body, [])
  for (const name of Object.keys(components)) componentSize(name, [])
  return total
}

export function lintInterface (spec: unknown): SpecLintResult {
  const result = s.validateWith(interfaceValidator(), spec)
  if (!result.valid) return { ...result, warnings: [], analysis: null }
  const doc = spec as MaxInterfaceSpec
  const issues: s.Issue[] = []
  const warnings: s.Issue[] = []
  const analysis = analyzeInterface(doc)
  const state = doc.state ?? {}
  const operations = doc.operations ?? {}

  walkInterfaceSpec(doc, ({ node, path, depth, ctx }) => {
    if (depth > MAX_DEPTH) issues.push({ path, message: `nested deeper than ${MAX_DEPTH} levels` })
    if (node.type === 'slot' && !ctx.component) issues.push({ path, message: 'slot is only used inside a component body' })
    // user became a scope after specs could already name a loop user; loop.user still works there
    if (typeof node.as === 'string' && node.as !== 'user' && (EXPR_SCOPES as readonly string[]).includes(node.as)) issues.push({ path: `${path}.as`, message: `"${node.as}" is a built-in scope name; choose another as name` })
    for (const [key, value] of iconUses(node)) {
      if (!(ICONS as readonly string[]).includes(value) && !doc.icons?.[value]) issues.push({ path: `${path}.${key}`, message: `icon "${value}" is neither a built-in icon nor declared in icons` })
    }
    if (node.type === 'use') {
      const def = doc.components?.[String(node.component)]
      if (!def) { issues.push({ path: `${path}.component`, message: `component "${String(node.component)}" is not declared in components` }); return }
      for (const key of Object.keys((node.with as Record<string, unknown> | undefined) ?? {})) {
        if (!(def.params ?? []).includes(key)) issues.push({ path: `${path}.with.${key}`, message: `is not a param of ${String(node.component)}; params: ${(def.params ?? []).join(', ') || 'none'}` })
      }
    }
  })
  for (const name of Object.keys(doc.icons ?? {})) {
    if ((ICONS as readonly string[]).includes(name)) issues.push({ path: `icons.${name}`, message: `"${name}" is a built-in icon; give the custom icon another name` })
  }
  const expanded = expandedSize(doc, issues)
  if (expanded > MAX_SPEC_NODES) issues.push({ path: 'body', message: `interface has ${expanded} nodes once components are expanded; the limit is ${MAX_SPEC_NODES}` })

  const { handlers, exprs } = collectInterface(doc)

  for (const use of stateUses(doc, handlers)) {
    const def = state[use.name]
    if (!def) { issues.push({ path: use.path, message: `state "${use.name}" is not declared` }); continue }
    if (use.need && !use.need.includes(def.type)) issues.push({ path: use.path, message: `needs ${use.need.join(' or ')} state; "${use.name}" is ${def.type}` })
  }

  for (const { expr, path, ctx } of exprs) {
    for (const ref of exprVars(expr)) {
      const [scope, name] = ref.path.split('.')
      if (scope === 'state') {
        if (name === undefined) issues.push({ path, message: 'read a named state, e.g. state.selected' })
        else if (!state[name]) issues.push({ path, message: `state "${name}" is not declared` })
      }
      if (scope === 'row' && !ctx.row) issues.push({ path, message: 'row is only available in datatable columns, row actions, calendar open and board open' })
      if (scope === 'result' && !ctx.result) issues.push({ path, message: 'result is only available in the then actions of callOperation' })
      if (scope === 'error' && !ctx.error) issues.push({ path, message: 'error is only available in the catch actions of callOperation' })
      if (scope === 'event' && !ctx.event) issues.push({ path, message: 'event is only available in field change actions' })
      if (scope === 'derived') {
        const declared = Object.keys(doc.derive ?? {})
        if (name === undefined || !declared.includes(name)) issues.push({ path, message: `derived "${name ?? ''}" is not declared in derive` })
        else if (ctx.derive && !ctx.derive.includes(name)) issues.push({ path, message: `derived "${name}" is declared later; a derive reads only the ones above it` })
      }
      if (scope === 'props') {
        const params = ctx.walk?.params
        if (!params) issues.push({ path, message: 'props is only available inside a component body' })
        else if (name === undefined || !params.includes(name)) issues.push({ path, message: `props "${name ?? ''}" is not a param of component ${ctx.walk?.component ?? ''}` })
      }
      if (scope === 'loop' && (name === undefined || !((ctx.walk?.loops ?? []).includes(name) || ref.loops.includes(name)))) {
        issues.push({ path, message: `loop "${name ?? ''}" is not the as name of an enclosing repeat, list or timeline, nor the name of an enclosing filter, map, reduce or other list body` })
      }
      if (scope === 'index' && !ctx.walk?.template && !ref.lambda) issues.push({ path, message: 'index is only available inside a repeat, list or timeline, or in the body of filter, map, some, all, count, find or reduce' })
      if (scope === 'acc' && !ref.reduce) issues.push({ path, message: 'acc is only available in the body of reduce' })
    }
    if (/\.(visible|disabled|when)$/.test(path) && exprVars(expr).some(ref => ref.scope === 'user')) {
      warnings.push({ path, message: 'user is who the POS says is signed in, for showing and recording names; it is not access control. Anyone can call the operations without this check: limit who can use the tool with its level instead' })
    }
  }

  const called = new Set<string>()
  for (const h of handlers) {
    if (h.nesting > MAX_ACTION_NESTING) issues.push({ path: h.path, message: `actions nested deeper than ${MAX_ACTION_NESTING} levels` })
    h.actions.forEach((a, i) => {
      const p = `${h.path}[${i}]`
      if (a.do === 'refresh' && h.origin === 'load') issues.push({ path: p, message: 'refresh reruns load, so load cannot contain it' })
      if (a.do === 'openPanel' && !doc.panels?.[String(a.panel)]) issues.push({ path: `${p}.panel`, message: `panel "${String(a.panel)}" is not declared in panels` })
      if ((a.do === 'openPanel' || a.do === 'copy') && h.origin === 'load') issues.push({ path: p, message: `${a.do} needs a user action; load cannot contain it` })
      if (a.do !== 'callOperation') return
      const name = String(a.operation)
      const ref = operations[name]
      if (!ref) { issues.push({ path: `${p}.operation`, message: `operation "${name}" is not declared in operations` }); return }
      called.add(name)
      const def = OPERATIONS[ref.operation]
      if (!def) return
      const input = (a.input as Record<string, unknown> | undefined) ?? {}
      for (const key of Object.keys(input)) {
        if (!def.input[key]) issues.push({ path: `${p}.input.${key}`, message: `is not an input of ${ref.operation}; allowed: ${Object.keys(def.input).join(', ')}` })
      }
      for (const [key, field] of Object.entries(def.input)) {
        if (field.required && input[key] === undefined) issues.push({ path: `${p}.input`, message: `${ref.operation} needs ${key}` })
      }
      const by = def.capabilityByInput
      if (by?.list && input[by.input] !== undefined && typeof input[by.input] !== 'string') {
        for (const [flag, caps] of Object.entries(by.capabilities)) {
          const options = Array.isArray(caps) ? caps : [caps]
          if (!options.some(c => (doc.capabilities ?? []).includes(c))) {
            warnings.push({ path: `${p}.input.${by.input}`, message: `is computed: POS refuses ${flag} unless the tool declares ${options.join(' or ')}` })
          }
        }
      }
      if (by && !by.list && typeof input[by.input] === 'string' && !by.capabilities[input[by.input] as string]) {
        issues.push({ path: `${p}.input.${by.input}`, message: `must be one of ${Object.keys(by.capabilities).join(', ')}` })
      }
      const literals = Object.fromEntries(Object.entries(input).filter(([key, v]) => def.input[key] && v !== null && typeof v !== 'object'))
      for (const problem of checkOperationInput(ref.operation, literals)) {
        if (problem.message !== 'is required') issues.push({ path: `${p}.input.${problem.path}`, message: problem.message })
      }
      for (const [key, field] of Object.entries(def.input)) {
        const v = literals[key]
        if (field.lenient && field.values && typeof v === 'string' && v !== '' && !field.values.includes(v)) {
          warnings.push({ path: `${p}.input.${key}`, message: `${ref.operation} supports ${field.values.join(', ')}; "${v}" is ignored` })
        }
      }
      if (ref.operation === 'metadata.get' || ref.operation === 'metadata.set' || ref.operation.endsWith('.list') || ref.operation.endsWith('.get')) {
        for (const use of metadataNamespaceUses(ref.operation, input, p)) {
          const ok = (doc.metadataNamespaces ?? []).includes(use.ns) || (use.mode === 'read' && doc.metadataRead === 'all')
          if (!ok) {
            issues.push({
              path: use.path,
              message: use.mode === 'write'
                ? `namespace "${use.ns}" is not in metadataNamespaces; a tool writes only the namespaces it declares`
                : `namespace "${use.ns}" is not in metadataNamespaces; declare it, or set metadataRead to all to read any namespace`
            })
          }
        }
      }
      if (def.mode === 'write' && h.origin === 'load') issues.push({ path: p, message: 'writes need a user action; load can only read' })
      if (a.when !== undefined && def.mode === 'write' && h.origin === 'load') issues.push({ path: `${p}.when`, message: 'when cannot hide a write on load' })
      if (def.mode === 'write' && h.origin.startsWith('filter: ')) issues.push({ path: p, message: 'filters run while the user types; save from a button instead' })
      if (typeof a.into === 'string' && def.mode === 'read' && state[a.into]) {
        const intoType = state[a.into]!.type
        if (intoType !== 'list' && intoType !== 'record' && intoType !== 'string') {
          warnings.push({ path: `${p}.into`, message: `read results are rows or a record; "${a.into}" is ${intoType}` })
        }
      }
    })
  }

  const declared = new Set(doc.capabilities)
  for (const cap of analysis.requiredCapabilities) {
    if (!declared.has(cap)) issues.push({ path: 'capabilities', message: `missing "${cap}", which the operations need` })
  }
  for (const cap of declared) {
    if (!analysis.requiredCapabilities.includes(cap)) warnings.push({ path: 'capabilities', message: `"${cap}" is declared but not used` })
  }
  for (const [name, ref] of Object.entries(operations)) {
    if (!called.has(name)) warnings.push({ path: `operations.${name}`, message: 'is declared but never called' })
    if (ref.confirmAbove !== undefined && OPERATIONS[ref.operation]?.mode !== 'write') issues.push({ path: `operations.${name}.confirmAbove`, message: 'only writes ask for confirmation' })
    if (ref.confirmAbove !== undefined && ref.confirmAbove > 0 && OPERATIONS[ref.operation]?.confirm === 'always') warnings.push({ path: `operations.${name}.confirmAbove`, message: `${ref.operation} always asks the user to confirm; confirmAbove is ignored` })
  }
  if (doc.refresh !== undefined && !interfaceReadTypes(doc).length) warnings.push({ path: 'refresh', message: 'refresh reruns on.load, which reads nothing' })
  if (analysis.risk !== 'read') warnings.push({ path: 'capabilities', message: `risk is "${analysis.risk}": a shop admin (level 7) must approve the interface in POS before it can be used` })
  lintCatalogBrowse(doc, handlers, warnings)
  for (const w of lintDesign(doc.body, { preset: doc.preset })) warnings.push({ path: w.path, message: `${w.message} (design: ${w.rule})` })

  return { valid: issues.length === 0, issues, warnings, analysis }
}

const OUTLINE_MAX = 100

function outlineLabel (node: Record<string, unknown>): string | null {
  const pick = (v: unknown) => typeof v === 'string' && v.trim() ? v.trim().slice(0, 120) : null
  switch (node.type) {
    case 'heading': case 'text': case 'callout': return pick(node.text)
    case 'field': case 'button': case 'metric': return pick(node.label)
    case 'filterbar': return pick(((node.filters as { label?: unknown }[] | undefined) ?? []).map(f => String(f.label ?? '')).join(', '))
    case 'calendar': return pick(node.kind)
    case 'board': return pick(node.source)
    default: return pick(node.title) ?? pick(node.label)
  }
}

export function describeInterface (spec: MaxInterfaceSpec): SpecDescription {
  const analysis = analyzeInterface(spec)
  const { handlers } = collectInterface(spec)
  const calledFrom = new Map<string, Set<string>>()
  for (const h of handlers) {
    for (const a of h.actions) {
      if (a.do !== 'callOperation') continue
      const name = String(a.operation)
      if (!calledFrom.has(name)) calledFrom.set(name, new Set())
      calledFrom.get(name)!.add(h.origin)
    }
  }
  const operations: SpecOperationFact[] = Object.entries(spec.operations ?? {}).flatMap(([name, ref]) => {
    const def = OPERATIONS[ref.operation]
    if (!def) return []
    const fact: SpecOperationFact = { name, operation: ref.operation, mode: def.mode, capability: def.capability, label: ref.label ?? null, calledFrom: [...(calledFrom.get(name) ?? [])] }
    if (def.mode === 'write') fact.confirmAbove = def.confirm === 'always' ? 0 : ref.confirmAbove ?? null
    return [fact]
  })
  const outline: SpecOutlineItem[] = []
  let total = 0
  walkInterfaceNodes(spec.body, ({ node, path }) => {
    total++
    if (outline.length >= OUTLINE_MAX) return
    outline.push({
      type: String(node.type),
      label: outlineLabel(node),
      sources: [],
      depth: (path.match(/\.children\[/g) ?? []).length,
      conditional: node.visible !== undefined,
      rules: 0
    })
  })
  return {
    title: spec.title,
    description: spec.description ?? null,
    tier: 'evaluate',
    risk: analysis.risk,
    personalData: analysis.personalData,
    approvalLevel: analysis.risk === 'read' ? analysis.minLevel : Math.max(analysis.minLevel, 7),
    capabilities: analysis.requiredCapabilities.map(id => ({ id, risk: CAPABILITY_RISK[id], level: CAPABILITY_LEVELS[id] })),
    sources: [],
    schedule: null,
    recipients: [],
    attachment: null,
    outline,
    outlineTruncated: total > outline.length,
    operations,
    state: Object.keys(spec.state ?? {}),
    metadataNamespaces: spec.metadataNamespaces ?? [],
    metadataRead: spec.metadataRead ?? 'declared'
  }
}

// ---------------------------------------------------------------------------
// State helpers shared by the runtime and hosts

export function coerceState (type: StateType, value: unknown): unknown {
  switch (type) {
    case 'string': return value === null || value === undefined || typeof value === 'object' ? '' : String(value).slice(0, 5000)
    case 'number': {
      const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN
      return Number.isFinite(n) ? n : null
    }
    case 'boolean': return value === true || value === 1 || value === 'true'
    case 'list': return Array.isArray(value) ? value.slice(0, MAX_LIST_STATE) : []
    case 'record': return value && typeof value === 'object' && !Array.isArray(value) ? value : null
    case 'map': {
      if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
      const out: Record<string, unknown> = {}
      for (const key of Object.keys(value).slice(0, MAX_LIST_STATE)) {
        if (MAP_KEY_RE.test(key)) out[key] = (value as Record<string, unknown>)[key]
      }
      return out
    }
  }
}

export function initialState (spec: MaxInterfaceSpec): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [name, def] of Object.entries(spec.state ?? {})) {
    const fallback = def.type === 'list' ? [] : def.type === 'map' ? {} : def.type === 'record' ? null : def.type === 'boolean' ? false : def.type === 'number' ? null : ''
    out[name] = def.initial === undefined ? fallback : coerceState(def.type, def.initial)
  }
  return out
}

/**
 * ISO from/to a daterange preset covers, counted from today (an ISO date). Empty for all,
 * custom and presets without a fixed window, which the operation resolves from period itself.
 */
export function daterangePresetRange (period: string, today: string): { from: string, to: string } {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(today)
  if (!m) return { from: '', to: '' }
  const y = +m[1]!
  const mo = +m[2]! - 1
  const d = +m[3]!
  const iso = (t: number) => new Date(t).toISOString().slice(0, 10)
  const day = (dd: number) => Date.UTC(y, mo, d + dd)
  // Calendar month/year steps clamp to the last day, as luxon plus() does (31.1. + 1 month = 28.2.)
  const plusMonths = (n: number) => {
    const last = new Date(Date.UTC(y, mo + n + 1, 0)).getUTCDate()
    return Date.UTC(y, mo + n, Math.min(d, last))
  }
  const weekday = (new Date(day(0)).getUTCDay() + 6) % 7
  const range = (from: number, to: number) => ({ from: iso(from), to: iso(to) })
  switch (period) {
    case 'today': return range(day(0), day(0))
    case 'yesterday': return range(day(-1), day(-1))
    case 'thisweek': return range(day(-weekday), day(6 - weekday))
    case 'lastweek': return range(day(-weekday - 7), day(-weekday - 1))
    case 'thismonth': return range(Date.UTC(y, mo, 1), Date.UTC(y, mo + 1, 0))
    case 'lastmonth': return range(Date.UTC(y, mo - 1, 1), Date.UTC(y, mo, 0))
    case 'thisyear': return range(Date.UTC(y, 0, 1), Date.UTC(y, 11, 31))
    case 'lastyear': return range(Date.UTC(y - 1, 0, 1), Date.UTC(y - 1, 11, 31))
    case 'overview':
    case 'starting':
    case 'startingtoday': return range(day(0), plusMonths(12))
    case 'startingtomorrow': return range(day(1), plusMonths(12))
    case 'startingthisweek': return range(day(0), day(7))
    case 'startingthismonth': return range(day(0), plusMonths(1))
    default: return { from: '', to: '' }
  }
}

/**
 * Fills the from/to state of every daterange filter whose period state holds a preset and whose
 * dates are still empty, so on.load already sees the dates the filter will show.
 */
export function fillDaterangeState (spec: MaxInterfaceSpec, state: Record<string, unknown>, today: string): void {
  walkInterfaceSpec(spec, ({ node }) => {
    if (node.type !== 'filterbar' || !Array.isArray(node.filters)) return
    for (const f of node.filters as Record<string, unknown>[]) {
      if (f?.input !== 'daterange' || typeof f.period !== 'string') continue
      const period = state[f.period]
      if (typeof period !== 'string' || !period) continue
      const from = typeof f.from === 'string' ? f.from : undefined
      const to = typeof f.to === 'string' ? f.to : undefined
      if ((!from || state[from]) && (!to || state[to])) continue
      const range = daterangePresetRange(period, today)
      if (!range.from || !range.to) continue
      if (from) state[from] = range.from
      if (to) state[to] = range.to
    }
  })
}

registerSpecKind(INTERFACE_KIND, {
  lint: lintInterface,
  analyze: spec => analyzeInterface(spec as MaxInterfaceSpec),
  describe: spec => describeInterface(spec as MaxInterfaceSpec)
})
